/**
 * Мастер «Наполнить справочники» — макет v2-13, утверждён founder:
 *   `docs/superpowers/prototypes/annual-planning/v2-13-catalog-fill-wizard.html`
 *   (ветка `docs/wireframes-season-planning-v2`, скриншот рядом).
 * Постановка founder дословно — `docs/superpowers/specs/2026-08-15-budget-catalogs-founder-edits.md`,
 * пункты 6-9 UC наполнения.
 *
 * Шаги макета «уровень → справочник» здесь уже пройдены: мастер открывается С
 * ЭКРАНА справочников, где раздел выбран вкладкой, а уровень — переключателем
 * хозяйства. Поэтому в мастере остаются три шага: способ → предпросмотр →
 * результат. Иначе экономист выбирал бы одно и то же дважды.
 *
 * Главное обещание предпросмотра: до нажатия «Загрузить» в справочнике НЕ
 * меняется ничего, а строки с ошибкой честно названы номером строки файла и не
 * поедут вовсе.
 */
import { useMemo, useState } from 'react';
import { AlertTriangle, Check, Download, FileSpreadsheet, Wand2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import {
  IMPORT_MAX_MB,
  IMPORT_STATUS_TITLES,
  SECTION_TITLES,
  TMC_SECTIONS,
  downloadImportTemplate,
  formatPrice,
  importErrorText,
  importableRows,
  useFillSection,
  useImportCommit,
  useImportPreview,
  type BudgetSection,
  type ImportPreview,
  type ImportRow,
  type ImportRowStatus,
} from '@/lib/planning/budgetCatalog';

export interface PriorSeason {
  season_id: string;
  year: number;
}

export interface FillWizardProps {
  section: BudgetSection;
  seasonId: string;
  /** Уровень ведения: хозяйство или шаблон компании (null). */
  farmId: string | null;
  farmName: string | null;
  priorSeasons: PriorSeason[];
  /**
   * Права РАЗНЫЕ на разных шагах: образец и предпросмотр положены читателю
   * (`budget_catalog:R`), а запись — нет (`import/commit` требует C+U, `fill` —
   * C). Поэтому мастер не закрывают целиком: отнимать у читателя возможность
   * посмотреть, что принесёт файл, незачем — гасим только терминальное
   * действие, чтобы человек не вложил работу и не получил отказ последним
   * шагом.
   */
  canCreate: boolean;
  canWrite: boolean;
  onManual: () => void;
  onClose: () => void;
}

type SourceKind = 'manual' | 'excel' | 'from_tenant_catalog' | 'copy_season' | 'from_company_template';
type Step = 'source' | 'preview' | 'done';

const SOURCES: { kind: SourceKind; title: string; description: string }[] = [
  {
    kind: 'manual',
    title: 'Ввести вручную',
    description: 'по позиции: название, единица, плановая цена, сопоставление с системным справочником',
  },
  {
    kind: 'excel',
    title: 'Загрузить экселем',
    description: 'скачать образец → заполнить своими данными → загрузить → посмотреть, что получится',
  },
  {
    kind: 'from_tenant_catalog',
    title: 'Взять из справочников компании',
    description: 'перенести действующие позиции компании в бюджет и проставить плановые цены',
  },
  {
    kind: 'from_company_template',
    title: 'Взять из шаблона компании',
    description: 'перенести в хозяйство позиции и цены, заведённые на уровне компании',
  },
  {
    kind: 'copy_season',
    title: 'Взять из прошлого бюджета',
    description: 'скопировать состав и цены прошлого сезона',
  },
];

const ROW_BADGE: Record<ImportRowStatus, { variant: 'default' | 'secondary' | 'outline' | 'destructive'; label: string }> = {
  new: { variant: 'default', label: 'новая' },
  changed: { variant: 'secondary', label: 'изменится' },
  unchanged: { variant: 'outline', label: 'как было' },
  error: { variant: 'destructive', label: 'ошибка' },
};

export function FillWizard({
  section,
  seasonId,
  farmId,
  farmName,
  priorSeasons,
  canCreate,
  canWrite,
  onManual,
  onClose,
}: FillWizardProps) {
  const [step, setStep] = useState<Step>('source');
  const [source, setSource] = useState<SourceKind>('excel');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [filter, setFilter] = useState<ImportRowStatus | 'all'>('all');
  const [fromSeasonId, setFromSeasonId] = useState<string | null>(priorSeasons[0]?.season_id ?? null);
  const [keepFarmLevel, setKeepFarmLevel] = useState(true);
  const [resetPrices, setResetPrices] = useState(false);
  const [result, setResult] = useState<{
    created: number;
    updated: number;
    unchanged: number;
    alreadyThere?: number;
  } | null>(null);

  const previewMutation = useImportPreview(section);
  const commitMutation = useImportCommit(section);
  const fillMutation = useFillSection(section);

  // Запись в справочник = завести позицию (C) и поправить существующую (U).
  const canImport = canCreate && canWrite;
  const fromTenantAvailable = TMC_SECTIONS.includes(section);
  const copyAvailable = priorSeasons.length > 0;
  // Переносить шаблон есть куда только когда открыт скоуп хозяйства: в самом
  // шаблоне компании эта кнопка означала бы «скопировать в себя».
  const fromTemplateAvailable = farmId !== null;
  const willImport = useMemo(() => (preview ? importableRows(preview.rows) : []), [preview]);
  const shownRows = useMemo(
    () => (preview ? preview.rows.filter((r) => filter === 'all' || r.status === filter) : []),
    [preview, filter],
  );
  const scopeLabel = farmId ? `хозяйство «${farmName ?? ''}»` : 'шаблон компании';

  async function handleTemplate() {
    try {
      await downloadImportTemplate(section);
    } catch (err) {
      toast.error(importErrorText(err));
    }
  }

  function handleFile(file: File | null) {
    if (!file) return;
    previewMutation.mutate(
      { file, seasonId, farmId },
      {
        onSuccess: (data) => {
          setPreview(data);
          setFilter('all');
          setStep('preview');
        },
      },
    );
  }

  function handleCommit() {
    if (!willImport.length) return;
    commitMutation.mutate(
      { seasonId, farmId, rows: willImport },
      {
        onSuccess: (data) => {
          setResult({ created: data.created, updated: data.updated, unchanged: data.unchanged });
          setStep('done');
        },
      },
    );
  }

  function handleFill(mode: 'copy_season' | 'from_tenant_catalog' | 'from_company_template') {
    fillMutation.mutate(
      {
        seasonId,
        mode,
        fromSeasonId: mode === 'copy_season' ? fromSeasonId : null,
        farmId: mode === 'from_company_template' ? farmId : null,
        keepFarmLevel,
        resetPrices,
      },
      {
        onSuccess: (data) => {
          setResult({
            created: data.created,
            updated: 0,
            unchanged: 0,
            alreadyThere: data.skipped_existing,
          });
          setStep('done');
        },
      },
    );
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-primary" />
            Наполнить справочник «{SECTION_TITLES[section]}»
          </DialogTitle>
          <DialogDescription>
            Наполняем {scopeLabel}. Способ можно поменять и потом — позиции складываются в один
            список.
          </DialogDescription>
        </DialogHeader>

        <ol className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {(
            [
              ['source', 'Способ'],
              ['preview', 'Предпросмотр'],
              ['done', 'Результат'],
            ] as const
          ).map(([key, title], idx) => (
            <li
              key={key}
              className={cn(
                'rounded-md border px-3 py-1',
                step === key ? 'border-primary bg-accent text-foreground' : 'border-border',
              )}
            >
              шаг {idx + 1} · {title}
            </li>
          ))}
        </ol>

        {step === 'source' && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              {SOURCES.map((option) => {
                const disabled =
                  (option.kind === 'copy_season' && !copyAvailable) ||
                  (option.kind === 'from_tenant_catalog' && !fromTenantAvailable) ||
                  (option.kind === 'from_company_template' && !fromTemplateAvailable);
                return (
                  <button
                    key={option.kind}
                    type="button"
                    aria-pressed={source === option.kind}
                    disabled={disabled}
                    onClick={() => setSource(option.kind)}
                    className={cn(
                      'rounded-md border p-3 text-left transition',
                      source === option.kind && !disabled
                        ? 'border-primary bg-accent'
                        : 'border-border hover:bg-muted',
                      disabled && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <span className="block text-sm font-medium text-foreground">{option.title}</span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {option.description}
                    </span>
                    {option.kind === 'copy_season' && !copyAvailable && (
                      <span className="mt-2 block text-xs text-muted-foreground">
                        Недоступно: прошлых бюджетов пока нет — появится со следующего сезона.
                      </span>
                    )}
                    {option.kind === 'from_tenant_catalog' && !fromTenantAvailable && (
                      <span className="mt-2 block text-xs text-muted-foreground">
                        Недоступно: расценки и цены готовой продукции заводятся прямо в бюджете.
                      </span>
                    )}
                    {option.kind === 'from_company_template' && !fromTemplateAvailable && (
                      <span className="mt-2 block text-xs text-muted-foreground">
                        Недоступно: сейчас открыт шаблон компании — выберите хозяйство, в которое
                        переносим.
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {source === 'excel' && (
              <div className="space-y-3 rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Образец «{SECTION_TITLES[section]}»: нужны только 3 колонки — Название, Ед. изм.,
                    Цена. Остальное можно не заполнять. Первая строка — заголовки, порядок колонок
                    значения не имеет.
                  </span>
                  <Button variant="outline" size="sm" className="ml-auto" onClick={handleTemplate}>
                    <Download className="mr-2 h-4 w-4" />
                    Скачать образец
                  </Button>
                </div>
                <div>
                  <Label htmlFor="fill-wizard-file" className="text-sm">
                    Ваш файл
                  </Label>
                  <input
                    id="fill-wizard-file"
                    data-testid="fill-wizard-file"
                    type="file"
                    accept=".xlsx,.xlsm,.csv"
                    disabled={previewMutation.isPending}
                    onChange={(e) => {
                      handleFile(e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                    className="mt-1 block w-full rounded-md border border-border bg-background p-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Excel (.xlsx, .xlsm) или .csv, до {IMPORT_MAX_MB} МБ и до 5 000 строк. Файл
                    только читаем — в справочник ничего не запишется, пока вы не посмотрите
                    предпросмотр.
                  </p>
                </div>
              </div>
            )}

            {source === 'copy_season' && copyAvailable && (
              <div className="space-y-3 rounded-md border border-border p-3">
                <div className="flex flex-wrap gap-2">
                  {priorSeasons.map((s) => (
                    <button
                      key={s.season_id}
                      type="button"
                      aria-pressed={fromSeasonId === s.season_id}
                      onClick={() => setFromSeasonId(s.season_id)}
                      className={cn(
                        'rounded-full border px-3 py-1 text-sm',
                        fromSeasonId === s.season_id
                          ? 'border-primary bg-accent text-foreground'
                          : 'border-border text-muted-foreground hover:bg-muted',
                      )}
                    >
                      Бюджет {s.year}
                    </button>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={keepFarmLevel}
                    onCheckedChange={(v) => setKeepFarmLevel(v === true)}
                  />
                  Наследовать видимость по хозяйствам — перенести как было
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={resetPrices}
                    onCheckedChange={(v) => setResetPrices(v === true)}
                  />
                  Перенести только состав, цены проставлю заново
                </label>
              </div>
            )}

            {source === 'from_company_template' && fromTemplateAvailable && (
              <div className="space-y-3 rounded-md border border-border p-3">
                <p className="text-sm text-muted-foreground">
                  Перенесём в хозяйство «{farmName ?? ''}» позиции, заведённые на уровне компании.
                  Уже заведённое в хозяйстве останется как есть — повторный запуск ничего не
                  задвоит.
                </p>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={resetPrices}
                    onCheckedChange={(v) => setResetPrices(v === true)}
                  />
                  Перенести только состав, цены проставлю заново
                </label>
              </div>
            )}

            {source === 'from_tenant_catalog' && fromTenantAvailable && (
              <p className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
                Перенесём действующие позиции компании в бюджет сезона. Плановые цены останутся
                пустыми — их проставите в справочнике. Повторный запуск ничего не задвоит.
              </p>
            )}
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Файл «{preview.file_name}» · строк в файле: {preview.counts.total}
            </p>

            {preview.columns.some((c) => !c.recognized) && (
              <p className="text-xs text-muted-foreground">
                Колонки, которые не грузим:{' '}
                {preview.columns
                  .filter((c) => !c.recognized)
                  .map((c) => c.source)
                  .join(', ')}
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
                Все · {preview.counts.total}
              </FilterChip>
              {(['new', 'changed', 'unchanged', 'error'] as ImportRowStatus[]).map((status) => (
                <FilterChip
                  key={status}
                  active={filter === status}
                  onClick={() => setFilter(status)}
                >
                  {IMPORT_STATUS_TITLES[status]} · {preview.counts[status]}
                </FilterChip>
              ))}
            </div>

            {preview.counts.error > 0 && (
              <p className="flex items-start gap-2 rounded-md border border-border bg-muted p-3 text-sm">
                <AlertTriangle className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  {preview.counts.error} строк(и) с ошибкой мы не загрузим — поправьте их в файле и
                  загрузите ещё раз. Остальные строки это не задерживает.
                </span>
              </p>
            )}

            <div className="max-h-[40vh] overflow-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Строка</TableHead>
                    <TableHead>Позиция</TableHead>
                    <TableHead className="w-24">Ед.</TableHead>
                    <TableHead className="w-32 text-right">Цена</TableHead>
                    <TableHead className="w-64">Что будет</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {shownRows.map((row) => (
                    <PreviewRow key={row.row_number} row={row} />
                  ))}
                  {shownRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-sm text-muted-foreground">
                        Таких строк в файле нет
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="space-y-2 rounded-md border border-border p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-foreground">
              <Check className="h-4 w-4 text-primary" />
              Готово
            </p>
            <p className="text-sm text-muted-foreground">
              Добавлено позиций: {result.created}
              {result.updated > 0 && `, обновлено: ${result.updated}`}
              {result.unchanged > 0 && `, оставлено без изменений: ${result.unchanged}`}
              {result.alreadyThere !== undefined &&
                result.alreadyThere > 0 &&
                `, уже было в справочнике: ${result.alreadyThere}`}
              .
            </p>
            <p className="text-xs text-muted-foreground">
              Новые позиции ждут сопоставления с системным справочником — они помечены «?» и видны в
              справочнике сразу.
            </p>
          </div>
        )}

        <DialogFooter>
          {step === 'source' && (
            <>
              <Button variant="ghost" onClick={onClose}>
                Отмена
              </Button>
              {source === 'manual' &&
                (canCreate ? (
                  <Button
                    onClick={() => {
                      onClose();
                      onManual();
                    }}
                  >
                    Завести позицию
                  </Button>
                ) : (
                  <ReadOnlyNote />
                ))}
              {source === 'copy_season' &&
                (canCreate ? (
                  <Button
                    disabled={!fromSeasonId || fillMutation.isPending}
                    onClick={() => handleFill('copy_season')}
                  >
                    Перенести из выбранного бюджета
                  </Button>
                ) : (
                  <ReadOnlyNote />
                ))}
              {source === 'from_tenant_catalog' &&
                (canCreate ? (
                  <Button
                    disabled={!fromTenantAvailable || fillMutation.isPending}
                    onClick={() => handleFill('from_tenant_catalog')}
                  >
                    Перенести из справочников компании
                  </Button>
                ) : (
                  <ReadOnlyNote />
                ))}
              {source === 'from_company_template' &&
                (canCreate ? (
                  <Button
                    disabled={!fromTemplateAvailable || fillMutation.isPending}
                    onClick={() => handleFill('from_company_template')}
                  >
                    Перенести из шаблона компании
                  </Button>
                ) : (
                  <ReadOnlyNote />
                ))}
              {source === 'excel' && previewMutation.isPending && (
                <span className="self-center text-sm text-muted-foreground">Читаем файл…</span>
              )}
            </>
          )}

          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('source')}>
                Назад
              </Button>
              {canImport ? (
                <Button
                  disabled={willImport.length === 0 || commitMutation.isPending}
                  onClick={handleCommit}
                >
                  {willImport.length > 0
                    ? `Загрузить ${willImport.length} позиций в справочник`
                    : 'Загружать нечего'}
                </Button>
              ) : (
                <ReadOnlyNote />
              )}
            </>
          )}

          {step === 'done' && <Button onClick={onClose}>Перейти в справочник</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Замена терминальной кнопки для читателя. Кнопку именно НЕ рендерим, а не
 * дизейблим: серая кнопка читается как «сейчас нельзя, попробуй позже», а
 * здесь дело не во времени, а в правах — это надо сказать словами.
 */
function ReadOnlyNote() {
  return (
    <span className="self-center text-sm text-muted-foreground">
      Загрузка доступна с правом правки справочников — предпросмотр можно смотреть и так.
    </span>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs',
        active ? 'border-primary bg-accent text-foreground' : 'border-border text-muted-foreground',
      )}
    >
      {children}
    </button>
  );
}

function PreviewRow({ row }: { row: ImportRow }) {
  const badge = ROW_BADGE[row.status];
  return (
    <TableRow>
      <TableCell className="text-muted-foreground">{row.row_number}</TableCell>
      <TableCell>
        <span className="text-foreground">{row.name || '—'}</span>
        {row.item_kind && (
          <span className="ml-2 text-xs text-muted-foreground">{row.item_kind}</span>
        )}
        {row.grade && <span className="ml-2 text-xs text-muted-foreground">{row.grade}</span>}
      </TableCell>
      <TableCell className="text-muted-foreground">{row.unit_text ?? '—'}</TableCell>
      <TableCell className="text-right tabular-nums">{formatPrice(row.planned_price)}</TableCell>
      <TableCell>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {row.status === 'changed' && row.changes.length > 0 && (
          <span className="ml-2 text-xs text-muted-foreground">
            поменяется: {row.changes.join(', ')}
          </span>
        )}
        {row.errors.map((err) => (
          <span key={err} className="ml-2 block text-xs text-destructive">
            {err}
          </span>
        ))}
      </TableCell>
    </TableRow>
  );
}
